import React from 'react';
import { Spinner, ProgressBar, Badge } from 'react-bootstrap';
import { useQueue } from '../context/QueueContext';
import './SectionQueue.css';

function computeProgress(job) {
    if (job.status === 'succeeded') return 100;
    if (job.status === 'failed') return 100;
    const steps = (job.progress || []).length;
    const pct = Math.round((Math.min(steps, 6) / 6) * 90) + 5;
    return Math.max(5, Math.min(95, pct));
}

function latestMessage(job) {
    const arr = job.progress || [];
    if (arr.length > 0 && arr[arr.length - 1]?.message) return arr[arr.length - 1].message;
    return job.status || 'Processing...';
}

function progressVariant(status) {
    if (status === 'succeeded') return 'success';
    if (status === 'failed') return 'danger';
    return 'primary';
}

function statusBadgeVariant(status) {
    if (status === 'succeeded') return 'success';
    if (status === 'failed') return 'danger';
    if (status === 'running') return 'primary';
    return 'secondary';
}

export default function SectionQueue() {
    const { jobs, removeJob, clearCompleted, collapsed, toggleCollapsed } = useQueue();

    if (jobs.length === 0) return null;

    const activeCount = jobs.filter(j => j.status === 'queued' || j.status === 'running').length;
    const doneCount = jobs.filter(j => j.status === 'succeeded' || j.status === 'failed').length;

    return (
        <div className={`sxi-queue-panel${collapsed ? ' sxi-queue-collapsed' : ''}`}>

            {/* ── Header ── */}
            <div className="sxi-queue-header" onClick={toggleCollapsed}>
                <div className="sxi-queue-header-left">
                    <svg
                        className="sxi-queue-header-icon"
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <polyline points="16 16 12 12 8 16" />
                        <line x1="12" y1="12" x2="12" y2="21" />
                        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                    </svg>
                    <span className="sxi-queue-title">Section Queue</span>
                    {activeCount > 0 && (
                        <span className="sxi-queue-badge-active">{activeCount}</span>
                    )}
                </div>
                <div className="sxi-queue-header-right">
                    {doneCount > 0 && !collapsed && (
                        <button
                            className="sxi-queue-clear-btn"
                            onClick={(e) => { e.stopPropagation(); clearCompleted(); }}
                        >
                            Clear Done
                        </button>
                    )}
                    <span className="sxi-queue-toggle-icon">{collapsed ? '▲' : '▼'}</span>
                </div>
            </div>

            {/* ── Job List ── */}
            {!collapsed && (
                <div className="sxi-queue-body">
                    {jobs.map(job => (
                        <div
                            key={job.jobId}
                            className={`sxi-queue-item sxi-queue-item--${job.status}`}
                        >
                            {/* Row 1: icon + name + badge + dismiss */}
                            <div className="sxi-queue-item-header">
                                <div className="sxi-queue-item-left">
                                    <div className="sxi-queue-status-icon">
                                        {(job.status === 'queued' || job.status === 'running') && (
                                            <Spinner size="sm" className="sxi-queue-spinner" />
                                        )}
                                        {job.status === 'succeeded' && (
                                            <span className="sxi-queue-icon-success">✓</span>
                                        )}
                                        {job.status === 'failed' && (
                                            <span className="sxi-queue-icon-failed">✕</span>
                                        )}
                                    </div>
                                    <div className="sxi-queue-item-info">
                                        <span className="sxi-queue-item-name" title={job.sectionName}>
                                            {job.sectionName}
                                        </span>
                                        <span className="sxi-queue-item-page" title={job.jobType === 'delete' ? 'Delete Section' : job.landingPage}>
                                            {job.jobType === 'delete' ? 'Delete Section' : `→ ${job.landingPage}`}
                                        </span>
                                    </div>
                                </div>
                                <div className="sxi-queue-item-right">
                                    <Badge
                                        bg={statusBadgeVariant(job.status)}
                                        className="sxi-queue-status-badge"
                                    >
                                        {job.status}
                                    </Badge>
                                    {(job.status === 'succeeded' || job.status === 'failed') && (
                                        <button
                                            className="sxi-queue-dismiss"
                                            title="Dismiss"
                                            onClick={() => removeJob(job.jobId)}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Row 2: progress bar + message */}
                            <div className="sxi-queue-item-progress">
                                <ProgressBar
                                    now={computeProgress(job)}
                                    variant={progressVariant(job.status)}
                                    animated={job.status === 'running' || job.status === 'queued'}
                                    striped={job.status === 'running' || job.status === 'queued'}
                                    className="sxi-queue-progress-bar"
                                />
                                <small className="sxi-queue-item-message">
                                    {latestMessage(job)}
                                </small>
                            </div>
                            {/* Conflict details for failed delete jobs */}
                            {job.jobType === 'delete' && job.status === 'failed' && job.conflicts?.length > 0 && (
                                <div className="sxi-queue-conflicts">
                                    {job.conflicts.map((c, i) => (
                                        <div key={i} className="sxi-queue-conflict-item">
                                            {c.type === 'campaign_rearrangement' && (
                                                <>
                                                    <span className="sxi-queue-conflict-label">Used in campaigns:</span>
                                                    <div className="sxi-queue-conflict-table-wrap">
                                                        <table className="sxi-queue-conflict-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Campaign</th>
                                                                    <th>Variant</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {c.used_in.map((u, ui) => (
                                                                    <tr key={ui}>
                                                                        <td title={u.campaign_name}>{u.campaign_name}</td>
                                                                        <td title={u.variant_name}>{u.variant_name}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            )}
                                            {c.type === 'saved_section' && (
                                                <>
                                                    <span className="sxi-queue-conflict-label">Saved on:</span>
                                                    <div className="sxi-queue-conflict-table-wrap">
                                                        <table className="sxi-queue-conflict-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Landing Page</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {c.landing_pages.map((lp, li) => (
                                                                    <tr key={li}>
                                                                        <td title={lp}>{lp}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}